import { useEffect, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import DialogComponent from "../../../../../../common/components/functional/dialogs/DialogComponent";
import isEmpty from "../../../../../../common/helpers/isEmpty";
import { ModalState } from "../../../../types/types";
import {
  PRICING_CATEGORY_KEYS,
  PricingCategory,
  PricingCategorySelection,
} from "../../../constants";
import LabelInputSelectRow from "../../../form/LabelInputSelectRow";
import PricingsByExpenseTypeForm from "../workAndAbscences/PricingsByExpenseTypeForm";
import { getPricingObjectForCustomerRecruit } from "../../../helpers/pricingUtils";
import DialogContentWrapper from "../../../../../../common/components/functional/dialogs/DialogContentWrapper";
import { PricingGuideText } from "../../../styles";

type FormValues = {
  baseHours: {
    multiplier: {
      type: PricingCategorySelection;
      value: string;
    };
    expenses: any[];
  };
  supplements: {
    multiplier: {
      type: PricingCategorySelection;
      value: string;
    };
    expenses: any[];
  };
};

type InputChangeHandlerProps = {
  fieldName: string;
  inputPath: string;
  newInput: string;
};

type SelectChangeHandlerProps = {
  fieldName: string;
  inputPath: string;
  selectPath: string;
  newSelection: string;
};

interface Props {
  title: string;
  tesId: string | null;
  disabled?: boolean;
  canEdit: boolean;
  onClose: () => void;
  formDataPathKey: string;
}

const START_PATH = "specials.customerRecruit";
const DRAG_HANDLE_SELECTOR = "drag-handle-work-and-absences";

export const CustomerRecruitPricing = ({
  title,
  tesId,
  disabled,
  canEdit,
  onClose,
  formDataPathKey,
}: Props) => {
  const { t } = useTranslation("customers");
  const { getValues, setValue } = useFormContext();

  const mainFormPricingValues = getValues(START_PATH);
  const mainCategoryValues = getValues("categories");

  const [modalState, setModalState] = useState<ModalState>({
    show: false,
    id: "",
  });

  const defaultValues = {
    ...PRICING_CATEGORY_KEYS.reduce(
      (acc, categoryKey) => {
        const existingMultiplier =
          mainFormPricingValues?.settings?.pricing?.[categoryKey]?.multiplier;
        const existingExpenses =
          mainFormPricingValues?.settings?.pricing?.[categoryKey]?.expenses;

        let multiplierType = "";
        let multiplierValue = "";
        let expenses = undefined;

        if (
          categoryKey === PricingCategory.BaseHours ||
          categoryKey === PricingCategory.Supplements
        ) {
          multiplierType =
            existingMultiplier?.type || PricingCategorySelection.Multiplier;
          multiplierValue = existingMultiplier?.value || "";
          expenses = existingExpenses || undefined;
        } else {
          // Define default values and expenses for other categories
          const mainCategoryMultiplierType =
            mainCategoryValues?.[categoryKey]?.multiplier?.type;
          const mainCategoryMultiplierValue =
            mainCategoryValues?.[categoryKey]?.multiplier?.value;
          const mainCategoryExpenses =
            mainCategoryValues?.[categoryKey]?.expenses;

          multiplierType =
            existingMultiplier?.type || mainCategoryMultiplierType;
          multiplierValue =
            existingMultiplier?.value || mainCategoryMultiplierValue;
          expenses = existingExpenses || mainCategoryExpenses;
        }

        acc[categoryKey] = {
          multiplier: {
            type: multiplierType,
            value: multiplierValue,
          },
          expenses,
        };
        return acc;
      },
      {} as Record<
        string,
        { multiplier: { type: string; value: string }; expenses: any }
      >,
    ),
  };

  const methods = useForm<FormValues>({
    defaultValues,
    mode: "onChange",
  });

  const {
    watch: watchEdit,
    handleSubmit,
    formState: { isDirty, isValid, errors },
    setError,
    clearErrors,
    getValues: getEditValues,
    setValue: setEditValue,
    trigger,
  } = methods;

  const onOpenModal = (id?: string) => setModalState({ show: true, id });
  const onCloseModal = () =>
    setModalState((prevState) => ({ ...prevState, show: false }));
  const onModalExited = () => {
    setModalState({ show: false, id: "" });
  };

  const getPricingBySalaryTypeModalDefaultProps = (
    categoryKey: PricingCategory,
  ) => {
    return {
      tesId: tesId!,
      mainFormPropertyKey: categoryKey,
      expenseTypeCategoryFilter: categoryKey,
      modalTitle: t("contract.pricingBySalaryType"),
      rowLabel: t("contract.salaryType"),
      inputLabel: t("contract.searchSalaryTypes"),
      addButtonLabel: t("contract.pricingBySalaryType"),
      confirmDialogTitle: t("contract.pricingBySalaryTypeDeleteConfirmTitle"),
      confirmDialogContentText: t(
        "contract.pricingBySalaryTypeDeleteConfirmText",
      ),
      onClose: onCloseModal,
      disabled,
      canEdit,
      dragHandleSelector: DRAG_HANDLE_SELECTOR,
    };
  };

  const getModalContent = () => {
    switch (modalState.id) {
      case PricingCategory.BaseHours:
        return (
          <PricingsByExpenseTypeForm
            {...getPricingBySalaryTypeModalDefaultProps(
              PricingCategory.BaseHours,
            )}
          />
        );
      case PricingCategory.Supplements:
        return (
          <PricingsByExpenseTypeForm
            {...getPricingBySalaryTypeModalDefaultProps(
              PricingCategory.Supplements,
            )}
          />
        );
      case PricingCategory.TaxFreeCompensations:
        return (
          <PricingsByExpenseTypeForm
            {...getPricingBySalaryTypeModalDefaultProps(
              PricingCategory.TaxFreeCompensations,
            )}
            modalTitle={t("contract.pricingByExpenseType")}
            rowLabel={t("contract.expenseType")}
            inputLabel={t("contract.searchExpenseTypes")}
            addButtonLabel={t("contract.pricingByExpenseType")}
            confirmDialogTitle={t(
              "contract.pricingByExpenseTypeDeleteConfirmTitle",
            )}
            confirmDialogContentText={t(
              "contract.pricingByExpenseTypeDeleteConfirmText",
            )}
          />
        );
      case PricingCategory.SickLeave:
        return (
          <PricingsByExpenseTypeForm
            {...getPricingBySalaryTypeModalDefaultProps(
              PricingCategory.SickLeave,
            )}
          />
        );
      // NOTE: "Sick leave billing rules" not in Phase 1.
      // case PricingModalType.SickLeaveBillingRules:
      //   return <SickLeaveBillingRulesForm onClose={onCloseModal} />;
      case PricingCategory.MidweekHolidays:
        return (
          <PricingsByExpenseTypeForm
            {...getPricingBySalaryTypeModalDefaultProps(
              PricingCategory.MidweekHolidays,
            )}
          />
        );
      case PricingCategory.ShortTimeWorkingScheme:
        return (
          <PricingsByExpenseTypeForm
            {...getPricingBySalaryTypeModalDefaultProps(
              PricingCategory.ShortTimeWorkingScheme,
            )}
          />
        );
      case PricingCategory.HolidaySalary:
        return (
          <PricingsByExpenseTypeForm
            {...getPricingBySalaryTypeModalDefaultProps(
              PricingCategory.HolidaySalary,
            )}
          />
        );
      default:
        return <></>;
    }
  };

  const handleInputChange = ({
    fieldName,
    inputPath,
    newInput,
  }: InputChangeHandlerProps) => {
    const currentType = getEditValues(`${fieldName}.multiplier.type` as any);
    if (currentType !== PricingCategorySelection.NotBilled) {
      !newInput
        ? setError(fieldName as any, {})
        : clearErrors(fieldName as any);
    }
    setEditValue(inputPath as any, newInput, { shouldDirty: true });
    trigger(inputPath as any);
  };

  const handleSelect = ({
    fieldName,
    inputPath,
    selectPath,
    newSelection,
  }: SelectChangeHandlerProps) => {
    newSelection === PricingCategorySelection.NotBilled
      ? clearErrors(fieldName as any)
      : setError(fieldName as any, {});
    setEditValue(selectPath as any, newSelection, { shouldDirty: true });
    // Reset input value when selection is changed
    setEditValue(inputPath as any, "", { shouldDirty: true });
    trigger(fieldName as any);
  };

  const getDefaultRowProps = (categoryKey: PricingCategory) => {
    const multiplierPath = `${categoryKey}.multiplier`;
    const typePath = `${multiplierPath}.type`;
    const valuePath = `${multiplierPath}.value`;
    const expensesPath = `${categoryKey}.expenses`;
    return {
      inputValue: watchEdit(`${categoryKey}.multiplier.value` as any),
      inputRequired: true,
      inputWidth: 1.5,
      inputUnitWidth: 0.2,
      onInputChange: (newInput: string) =>
        handleInputChange({
          fieldName: categoryKey,
          inputPath: `${categoryKey}.multiplier.value`,
          newInput,
        }),
      defaultSelection: getEditValues(typePath as any),
      onSelectionChange: (newSelection: string) =>
        handleSelect({
          fieldName: categoryKey,
          inputPath: valuePath,
          selectPath: typePath,
          newSelection,
        }),
      showButton:
        watchEdit(typePath as any) !== PricingCategorySelection.NotBilled &&
        !!tesId,
      showViewButton: watchEdit(expensesPath as any),
      editButtonText: t("contract.pricingBySalaryType"),
      viewButtonText: t("contract.showPricingBySalaryTypeInformation"),
      onButtonClick: () => onOpenModal(categoryKey),
      disabled: disabled,
    };
  };

  // Set errors for categories which don't have valid values
  useEffect(() => {
    PRICING_CATEGORY_KEYS.forEach((categoryKey) => {
      const categoryObject = getEditValues(categoryKey as any);
      const categoryObjectMultiplier = categoryObject?.multiplier;
      if (
        categoryObjectMultiplier?.type !== PricingCategorySelection.NotBilled &&
        isEmpty(categoryObjectMultiplier?.value)
      ) {
        setError(categoryKey as any, {});
      }
    });
    // Call useEffect only once onmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (formData: any) => {
    const updatedObject = {
      enabled: mainFormPricingValues.enabled,
      settings: {
        pricing: getPricingObjectForCustomerRecruit({ formData }),
      },
    };

    setValue(formDataPathKey, updatedObject, {
      shouldDirty: true,
    });

    onClose();
  };

  const onSave = () => {
    handleSubmit(onSubmit)();
  };

  return (
    <DialogContentWrapper
      title={title}
      infoText={t("contract.customerRecruitPricingInfo")}
      onCancel={onClose}
      onSave={onSave}
      saveDisabled={!isDirty || !isValid || !isEmpty(errors)}
      containerSx={{ marginLeft: "1rem" }}
    >
      <PricingGuideText>
        {t("contract.customerRecruitGuideText")}
      </PricingGuideText>
      <LabelInputSelectRow
        label={t("settings:BaseHours")}
        selectOptions={[PricingCategorySelection.Multiplier]}
        {...getDefaultRowProps(PricingCategory.BaseHours)}
      />
      <LabelInputSelectRow
        label={t("settings:Supplements")}
        selectOptions={[PricingCategorySelection.Multiplier]}
        {...getDefaultRowProps(PricingCategory.Supplements)}
      />
      <LabelInputSelectRow
        label={t("settings:TaxFreeCompensations")}
        {...getDefaultRowProps(PricingCategory.TaxFreeCompensations)}
        editButtonText={t("contract.pricingByExpenseType")}
        viewButtonText={t("contract.showPricingByExpenseTypeInformation")}
      />
      <LabelInputSelectRow
        label={t("customers:contract.SickLeaves")}
        {...getDefaultRowProps(PricingCategory.SickLeave)}
        // NOTE: "Sick leave billing rules" not in Phase 1.
        // NOTE2: After the change that every row (category) has own button, we need to
        // make changes here to make "Sick leave billing rules" possible.
        // showButton
        // showViewButton={!!watch("sickLeaveBillingRules")} // TODO rule for showing correct mode
        // TODO need to show view button here, but fields should not be disabled?
        // -> If this is the case, need to modify <LabelInputSelectRow /> component
        // editButtonText={t("contract.sickLeaveBillingRules")}
        // viewButtonText={t("contract.showSickLeaveBillingRulesInformation")}
        // onButtonClick={() =>
        //   onOpenModal(PricingModalType.SickLeaveBillingRules)
        // }
      />
      <LabelInputSelectRow
        label={t("settings:MidweekHolidays")}
        {...getDefaultRowProps(PricingCategory.MidweekHolidays)}
      />
      <LabelInputSelectRow
        label={t("settings:ShortTimeWorkingScheme")}
        {...getDefaultRowProps(PricingCategory.ShortTimeWorkingScheme)}
      />
      <LabelInputSelectRow
        label={t("customers:contract.HolidaySalary")}
        {...getDefaultRowProps(PricingCategory.HolidaySalary)}
      />
      <LabelInputSelectRow
        label={t("settings:HolidayBonus")}
        {...getDefaultRowProps(PricingCategory.HolidayBonus)}
        showButton={false}
      />
      <FormProvider {...methods}>
        <DialogComponent
          open={modalState.show}
          onClose={onCloseModal}
          sx={{ display: "flex", maxHeight: "80%" }}
          dragHandleSelector={DRAG_HANDLE_SELECTOR}
          TransitionProps={{ onExited: onModalExited }}
        >
          {getModalContent()}
        </DialogComponent>
      </FormProvider>
    </DialogContentWrapper>
  );
};
